<script lang="ts">
    import { fly } from "svelte/transition";

    import { activeTool, activeToolOptions, availableTools } from "$lib/state";
    import type { ToolInfo } from "$lib/panels/tools";
    import SymmetryPopover from "$lib/panels/SymmetryPopover.svelte";
    import Icon from "$lib/utility/Icon.svelte";
    import Popup from "$lib/utility/Popup.svelte";


    function update(name: string) {
        activeToolOptions.optionChanged(name);
    }

    const missing: ToolInfo["options"][string] = {
        name: "MISSING",
        icon: "missing-24",
    };

    let timeout: any;
    let overflow = "visible";

    function optionsAnimation(
        node: HTMLElement,
        { duration = 200, y = 0, direction = "up" as "up" | "down" } = {},
    ) {
        clearTimeout(timeout);
        overflow = "hidden";
        timeout = setTimeout(() => overflow = "visible", duration);

        return fly(node, {
            duration,
            y: direction === "down" ? y : -y,
        });
    }

    $: tool = $availableTools?.[$activeTool];
</script>

<!----------------------------------------------------------------------------->

<div class="tool-options">
    <div class="tool-option-list" style="overflow: {overflow};">
        {#each [...($activeToolOptions?.entries() ?? [])].sort() as [id, option], index (id + index)}
            {@const optionInfo = tool.options[id] ?? missing}

            <label class="option" title={optionInfo.name} in:optionsAnimation='{{ y: 32, direction: "down" }}'
                   out:optionsAnimation='{{ y: 32, direction: "up" }}'>
                {#if option.type === "Number"}
                    {@const icon = option.value ? optionInfo.icon : optionInfo.iconOff ?? optionInfo.icon}
                    <Popup>
                        <Icon slot="button" let:open id={icon} on:click={open} title={optionInfo.name} />

                        <div class="popup number-popup">
                            <span>{optionInfo.name}</span>
                            <span>
                        <span class="value" style="width: {option.max.toString().length + 2}ch">
                            {option.value}
                        </span>
                                {#if optionInfo.unit}
                                <span class="unit">{optionInfo.unit}</span>
                            {/if}
                    </span>
                            <div class="input">
                                <input type="range"
                                       bind:value={option.value}
                                       min={option.min}
                                       max={option.max}
                                       on:input={() => update(id)}
                                />
                            </div>
                        </div>
                    </Popup>
                {:else if option.type === "Bool"}
                    {@const hasOffIcon = optionInfo.iconOff !== undefined}
                    {@const icon = option.value ? optionInfo.icon : optionInfo.iconOff ?? optionInfo.icon}
                    <Icon id={icon} />
                    <input
                        type="checkbox"
                        class:colorize={!hasOffIcon}
                        bind:checked={option.value}
                        on:change={() => update(id)}
                    />
                {/if}
            </label>
        {/each}
    </div>

    <div class="global-options">
        <SymmetryPopover />
    </div>
</div>

<!----------------------------------------------------------------------------->

<style>
    .tool-options {
        display: grid;
        grid-template-columns: minmax(0, 1fr) max-content;
        align-items: center;
        gap: 0.5em;
        background: var(--panel-bg);
        z-index: 1;
        height: 2em;
        padding-inline: 0.25em;
        position: relative;
    }

    .tool-option-list {
        display: grid;
        grid-auto-columns: max-content;
        grid-auto-flow: column;
        align-items: center;
        gap: 0.5em;
        min-width: 0;
        height: 100%;
    }

    .global-options {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        height: 100%;
    }

    .option {
        display: flex;
        align-items: center;
    }

    .option:has(input[type="checkbox"]) {
        &:has(.colorize) {
            color: var(--t400);
        }

        &:has(.colorize:checked) {
            color: var(--tacc);
        }

        & input {
            display: none;
        }
    }

    .popup {
        position: absolute;
        display: flex;
        flex-direction: column;
        align-items: center;
        background: var(--panel-bg);

        top: calc(100% + 2px);
        margin-left: -0.25em;
    }

    .number-popup {
        display: grid;
        grid-template:
            "name  size" max-content
            "range range" max-content
            /max-content   max-content;
        padding: 0.25em;
        row-gap: 0.25em;

        & .input {
            width: 100%;
            grid-area: range;
            display: flex;
        }

        & input[type=range] {
            flex-grow: 1;
            width: 5em;
        }

        & .value {
            display: inline-block;
            text-align: right;
        }

        & .unit {
            color: var(--t400);
        }
    }
</style>
